import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input } from '@jaylabs/ui';
import Logo from '../components/Logo';
import { API_URL } from '../utils/apiUrl';
import './Auth.css';

function PasswordReset() {
  const bgImage = localStorage.getItem('dashboardBg') || '/backgrounds/Background-2-1024x682.jpg';
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [requestStatus, setRequestStatus] = useState('');
  const [requestOk, setRequestOk] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStatus, setResetStatus] = useState('');
  const [resetOk, setResetOk] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    setRequestStatus('');
    setRequestOk(false);
    if (!email.trim()) {
      setRequestStatus('Email is required.');
      return;
    }
    setRequestLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setRequestOk(true);
        setRequestStatus(data.message || 'Reset email sent. Check your inbox.');
      } else {
        setRequestStatus(data.message || 'Failed to request reset.');
      }
    } catch {
      setRequestStatus('Error connecting to server.');
    } finally {
      setRequestLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setResetStatus('');
    setResetOk(false);
    if (!resetToken.trim() || !newPassword.trim()) {
      setResetStatus('Token and new password are required.');
      return;
    }
    setResetLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken.trim(), new_password: newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setResetOk(true);
        setResetStatus('Password reset successfully! You can now log in.');
      } else {
        setResetStatus(data.message || 'Failed to reset password.');
      }
    } catch {
      setResetStatus('Error connecting to server.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div
      className="dd-auth-page"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.65), rgba(255,255,255,0.65)), url(${bgImage})`
      }}
    >
      <div className="dd-auth-card dd-auth-card-wide">
        <button className="dd-auth-back" onClick={() => navigate('/login')}>
          ← Back to Login
        </button>

        <div className="dd-auth-logo">
          <Logo size="lg" clickable={false} />
        </div>
        <h1 className="dd-auth-title">Reset Password</h1>
        <p className="dd-auth-subtitle">Enter your email to receive a reset link</p>

        {/* Step 1: Request reset */}
        <form onSubmit={requestReset}>
          <p className="dd-auth-section-title">Step 1 — Request a reset token</p>

          <div className="dd-auth-field">
            <label htmlFor="reset-email" className="dd-auth-label">Email Address</label>
            <Input
              id="reset-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              size="lg"
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={requestLoading}
            style={{ width: '100%' }}
          >
            Send Reset Email
          </Button>

          {requestStatus && (
            <div className={`dd-auth-status ${requestOk ? 'dd-auth-status-success' : 'dd-auth-status-error'}`}>
              {requestStatus}
            </div>
          )}
        </form>

        <hr className="dd-auth-divider" />

        {/* Step 2: Use token */}
        <form onSubmit={submitReset}>
          <p className="dd-auth-section-title">Step 2 — Enter your new password</p>

          <div className="dd-auth-field">
            <label htmlFor="reset-token" className="dd-auth-label">Reset Token</label>
            <Input
              id="reset-token"
              type="text"
              value={resetToken}
              onChange={(e) => setResetToken(e.target.value)}
              placeholder="Paste token from email"
              required
              size="lg"
              style={{ fontFamily: 'monospace' }}
            />
          </div>

          <div className="dd-auth-field">
            <label htmlFor="reset-new-password" className="dd-auth-label">New Password</label>
            <Input
              id="reset-new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="10+ characters"
              required
              size="lg"
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={resetLoading}
            style={{ width: '100%' }}
          >
            Reset Password
          </Button>

          {resetStatus && (
            <div className={`dd-auth-status ${resetOk ? 'dd-auth-status-success' : 'dd-auth-status-error'}`}>
              {resetStatus}
              {resetOk && (
                <span>
                  {' '}
                  <a href="/login" style={{ color: '#15803d', fontWeight: 600 }}>Log in now →</a>
                </span>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default PasswordReset;
